import { useEffect } from "react";
import { Lock, CreditCard, Calendar, ArrowLeft, Star, TrendingUp, Users, MessageSquare, ThumbsUp, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCalApi } from "@calcom/embed-react";

interface SubscriptionLockOverlayProps {
  backTo?: string;
  backLabel?: string;
}

const formatNumber = (num: number): string => {
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return num.toString();
};

const StatCard = ({ icon: Icon, label, value, sublabel }: { 
  icon: React.ElementType; label: string; value: string; sublabel?: string 
}) => (
  <div className="flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-muted/30 p-3">
    <Icon className="h-4 w-4 text-primary" />
    <span className="text-lg font-bold text-foreground">{value}</span>
    <span className="text-[11px] text-muted-foreground leading-tight text-center">{label}</span>
    {sublabel && <span className="text-[10px] text-muted-foreground/70">{sublabel}</span>}
  </div>
);

const StatSkeleton = () => (
  <div className="flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-muted/30 p-3">
    <Skeleton className="h-4 w-4 rounded-full" />
    <Skeleton className="h-5 w-10" />
    <Skeleton className="h-3 w-16" />
  </div>
);

export const SubscriptionLockOverlay = ({ backTo, backLabel }: SubscriptionLockOverlayProps = {}) => {
  const navigate = useNavigate();
  const { hasNoSubscription, isExpired, isAwaitingTrialPayment } = useSubscriptionStatus();
  const { workspace, refetch } = useWorkspace();

  // Check if workspace has any payment history
  const { data: invoiceData, isLoading: invoicesLoading } = useQuery({
    queryKey: ["workspace-invoice-check", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return { hasInvoices: false };
      const { data } = await supabase
        .from("invoices")
        .select("id")
        .eq("workspace_id", workspace.id)
        .limit(1);
      return { hasInvoices: (data?.length ?? 0) > 0 };
    },
    enabled: !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  });


  // Initialize Cal.com
  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "linkedin" });
      cal("ui", {
        theme: "dark",
        styles: { branding: { brandColor: "#6366f1" } },
        hideEventTypeDetails: false,
        layout: "month_view"
      });
    })();
  }, []);

  // Fetch workspace campaign stats (only when expired)
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["expired-workspace-stats", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null;

      // Get all client IDs for this workspace
      const { data: clients } = await supabase
        .from("clients")
        .select("id")
        .eq("workspace_id", workspace.id);

      if (!clients?.length) return null;

      const clientIds = clients.map(c => c.id);

      // Get all campaign IDs for these clients
      const { data: campaigns } = await supabase
        .from("campaigns")
        .select("id, created_at")
        .in("client_id", clientIds)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });

      if (!campaigns?.length) return null;

      const campaignIds = campaigns.map(c => c.id);
      const earliestCampaign = campaigns[0].created_at;

      // Get aggregate stats from the view
      const { data: aggStats } = await supabase
        .from("campaign_stats_aggregate")
        .select("connection_sent_count, connection_accepted_count, reply_received_count, messaged_count")
        .in("campaign_id", campaignIds);

      // Sum up all stats
      const totals = (aggStats || []).reduce((acc, s) => ({
        sent: acc.sent + (Number(s.connection_sent_count) || 0),
        accepted: acc.accepted + (Number(s.connection_accepted_count) || 0),
        replies: acc.replies + (Number(s.reply_received_count) || 0),
        messaged: acc.messaged + (Number(s.messaged_count) || 0),
      }), { sent: 0, accepted: 0, replies: 0, messaged: 0 });

      // Get positive reply intents
      const { count: positiveReplies } = await supabase
        .from("targets")
        .select("id", { count: "exact", head: true })
        .in("campaign_id", campaignIds)
        .eq("reply_intent", "positive");

      // Days active
      const daysActive = Math.max(1, Math.floor(
        (Date.now() - new Date(earliestCampaign).getTime()) / (1000 * 60 * 60 * 24)
      ));

      const acceptanceRate = totals.sent > 0 
        ? ((totals.accepted / totals.sent) * 100).toFixed(1) 
        : "0";

      // Projected connections in 3 months based on daily rate
      const dailySendRate = totals.sent / daysActive;
      const projectedConnections = Math.round(dailySendRate * 90 * (totals.accepted / Math.max(totals.sent, 1)));

      return {
        ...totals,
        positiveReplies: positiveReplies || 0,
        daysActive,
        acceptanceRate,
        projectedConnections,
        totalCampaigns: campaigns.length,
      };
    },
    enabled: isExpired && !!workspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  const openCalBooking = () => {
    (async function () {
      const cal = await getCalApi({ namespace: "linkedin" });
      cal("modal", {
        calLink: "wdcweb/linkedin"
      });
    })();
  };

  // Determine messaging based on subscription state
  const isNewWorkspace = hasNoSubscription && !isExpired;
  const title = isNewWorkspace ? "Subscription Required" : "Subscription Expired";
  const description = isNewWorkspace 
    ? "Subscribe to a plan to unlock all features and start growing your network."
    : "Your subscription has expired. Please renew to continue accessing this feature.";

  const hasStats = stats && stats.sent > 0;

  // Show loading spinner while checking invoice history
  if (invoicesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[85vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Managed workspace gate: per-account billing, no Razorpay/trial wall.
  if (workspace?.settings?.simplified_onboarding_enabled === true) {
    return (
      <div className="flex items-center justify-center min-h-[85vh] px-4">
        <Card className="w-full max-w-md text-center p-8">
          <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Accounts need renewing</h2>
          <p className="text-muted-foreground mb-6">
            Please contact your account manager to renew your accounts.
          </p>
          <Button variant="outline" onClick={openCalBooking} className="w-full">
            <Calendar className="w-4 h-4 mr-2" />
            Book a Meeting
          </Button>
        </Card>
      </div>
    );
  }



  return (
    <div className="flex items-center justify-center min-h-[85vh] px-4">
      <Card className={`w-full text-center p-8 ${isExpired && hasStats ? 'max-w-2xl' : 'max-w-md'}`}>
        <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        <p className="text-muted-foreground mb-6">
          {description}
        </p>

        {/* Performance Stats (expired state only) */}
        {isExpired && (statsLoading ? (
          <div className="mb-6">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your Results So Far</p>
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, i) => <StatSkeleton key={i} />)}
            </div>
          </div>
        ) : hasStats ? (
          <div className="mb-6">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your Results So Far</p>
            <div className="grid grid-cols-3 gap-2">
              <StatCard icon={Send} label="Connections Sent" value={formatNumber(stats.sent)} />
              <StatCard icon={Users} label="Accepted" value={formatNumber(stats.accepted)} />
              <StatCard icon={MessageSquare} label="Replies" value={formatNumber(stats.replies)} />
              <StatCard icon={TrendingUp} label="Acceptance Rate" value={`${stats.acceptanceRate}%`} />
              <StatCard icon={ThumbsUp} label="Interested Leads" value={formatNumber(stats.positiveReplies)} />
              <StatCard icon={Clock} label="Days Active" value={stats.daysActive.toString()} />
            </div>

            {/* Projected Growth */}
            {stats.projectedConnections > 0 && (
              <div className="mt-4 rounded-lg bg-primary/5 border border-primary/20 p-3">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <span className="text-foreground">
                    Renew & gain <span className="font-bold text-primary">~{formatNumber(stats.projectedConnections)}</span> more connections in 3 months
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : null)}

        <div className="flex flex-col gap-3">
          <Button onClick={openCalBooking} className="w-full">
            <CreditCard className="w-4 h-4 mr-2" />
            Contact your account manager to renew
          </Button>
          <Button variant="outline" onClick={openCalBooking} className="w-full">
            <Calendar className="w-4 h-4 mr-2" />
            Book a Meeting
          </Button>
          {backTo && (
            <Button variant="ghost" onClick={() => navigate(backTo)} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {backLabel || "Go Back"}
            </Button>
          )}
        </div>

        {/* Social Proof */}
        <div className="mt-8 pt-6 border-t border-border/50">
          <p className="text-xs font-medium text-muted-foreground mb-4 uppercase tracking-wider">Trusted by industry leaders</p>
          
          <div className="flex flex-col items-center gap-4">
            <div className="flex -space-x-2">
              {[
                { name: "Tata", src: "/logos/tata.png" },
                { name: "Emami", src: "/logos/emami.png" },
                { name: "Slack", src: "/logos/slack.svg" },
                { name: "Volkswagen", src: "/logos/volkswagen.png" }
              ].map((logo, index) => (
                <div 
                  key={logo.name} 
                  className={`relative w-8 h-8 rounded-full border-2 border-background bg-white flex items-center justify-center ring-1 ring-border shadow-sm overflow-hidden ${logo.name === 'Emami' ? 'p-0.5' : 'p-1.5'}`}
                  style={{ zIndex: 10 - index }}
                >
                  <img 
                    src={logo.src} 
                    alt={logo.name} 
                    className="w-full h-full object-contain" 
                  />
                </div>
              ))}
            </div>
            
            <div className="flex items-center gap-1.5">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">2,000+</span> growth teams
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
